import { getNlsString } from '../../nls/nlsRuntime'
import type { NlsInterpolationValues } from '../../nls/nlsTypes'
import type { NlsKey } from '../../nls/strings_nls'
import type { ChatAuthorRole } from '../../types/chat'

export type TeamChatPlatform = 'desktop' | 'member' | 'helper'

export const DEFAULT_CHAT_CHANNEL_ID = 'workspace-chat'

export type ChatNlsT = (key: NlsKey, values?: NlsInterpolationValues) => string

const defaultChatT: ChatNlsT = (key, values) => getNlsString(key, values, 'en')

export const CHAT_SHELL_COPY = {
  pageLabel: 'Chat Hub',
  sidebarTitle: 'Chats and discussions',
  activeChannelsHeading: 'Channels',
  archivedChannelsHeading: 'Archived channels',
  channelListModeLabel: 'Channel list mode',
  activeModeLabel: 'Active',
  archiveModeLabel: 'Archive',
  channelFilterPlaceholder: 'Filter channels',
  channelFilterAriaLabel: 'Filter channels',
  noArchivedChannels: 'No archived channels',
  noChannels: 'No channels yet',
  noChannelMatches: 'No channel matches',
  tagsHeading: 'Tags',
  tagFilterPlaceholder: 'Filter tags',
  tagFilterAriaLabel: 'Filter tags',
  allChatsLabel: 'All chats',
  noHashtags: 'No hashtags yet',
  noTagMatches: 'No tag matches',
  closeChannelsLabel: 'Close channels',
  showChannelsLabel: 'Show channels',
  showMembersLabel: 'Show members and details',
  closeMembersLabel: 'Close members',
  aiTriggerLabel: 'AI',
  aiControlsLabel: 'AI controls',
  closeAiControlsLabel: 'Close AI controls',
  selectChatsForAiLabel: 'select chats for AI',
  sendSelectedChatsToAiLabel: 'Send to AI',
  createChannelLabel: 'Create channel',
  editChannelTitle: 'Edit channel',
  archiveChannelTitle: 'Archive channel',
  restoreChannelTitle: 'Restore channel',
  deleteForeverTitle: 'Delete forever',
  unreadLabel: 'Unread',
  lockedNotice: 'Chat unlocks once at least two people (members or helpers) have joined.',
  archivedNotice: 'This channel is archived. Restore it from the archive before sending new messages.',
  emptyDesktopChannel: 'No messages in this channel yet.',
  emptyHelperChannel: 'No messages are visible in this channel yet. Use the channel menu to switch conversations if your notification opened a different thread.',
  archivedComposerPlaceholder: 'Archived channel',
  activeComposerPlaceholder: 'Message all chats',
  lockedComposerPlaceholder: 'Waiting for one more person (member or helper)',
} as const

export type ChatShellCopy = { [K in keyof typeof CHAT_SHELL_COPY]: string }

export const getChatShellCopy = (t: ChatNlsT = defaultChatT): ChatShellCopy => ({
  pageLabel: t('NLSChatPageLabel'),
  sidebarTitle: t('NLSChatLabelChatsDiscussions'),
  activeChannelsHeading: t('NLSChatLabelChannels'),
  archivedChannelsHeading: t('NLSChatLabelArchivedChannels'),
  channelListModeLabel: t('NLSChatLabelChannelListMode'),
  activeModeLabel: t('NLSChatLabelActive'),
  archiveModeLabel: t('NLSChatLabelArchive'),
  channelFilterPlaceholder: t('NLSChatFilterChannelsPlaceholder'),
  channelFilterAriaLabel: t('NLSChatFilterChannelsAria'),
  noArchivedChannels: t('NLSChatEmptyNoArchivedChannels'),
  noChannels: t('NLSChatEmptyNoChannels'),
  noChannelMatches: t('NLSChatEmptyNoChannelMatches'),
  tagsHeading: t('NLSChatLabelTags'),
  tagFilterPlaceholder: t('NLSChatFilterTagsPlaceholder'),
  tagFilterAriaLabel: t('NLSChatFilterTagsAria'),
  allChatsLabel: t('NLSChatLabelAllChats'),
  noHashtags: t('NLSChatEmptyNoHashtags'),
  noTagMatches: t('NLSChatEmptyNoTagMatches'),
  closeChannelsLabel: t('NLSChatActionCloseChannels'),
  showChannelsLabel: t('NLSChatActionShowChannels'),
  showMembersLabel: t('NLSChatActionShowMembers'),
  closeMembersLabel: t('NLSChatActionCloseMembers'),
  aiTriggerLabel: t('NLSChatLabelAi'),
  aiControlsLabel: t('NLSChatLabelAiControls'),
  closeAiControlsLabel: t('NLSChatActionCloseAiControls'),
  selectChatsForAiLabel: t('NLSChatActionSelectChatsForAi'),
  sendSelectedChatsToAiLabel: t('NLSChatActionSendSelectedChatsToAi'),
  createChannelLabel: t('NLSChatActionCreateChannel'),
  editChannelTitle: t('NLSChatTitleEditChannel'),
  archiveChannelTitle: t('NLSChatTitleArchiveChannel'),
  restoreChannelTitle: t('NLSChatActionRestore'),
  deleteForeverTitle: t('NLSChatActionDeleteForever'),
  unreadLabel: t('NLSChatLabelUnread'),
  lockedNotice: t('NLSChatNoticeLocked'),
  archivedNotice: t('NLSChatNoticeArchived'),
  emptyDesktopChannel: t('NLSChatEmptyNoMessages'),
  emptyHelperChannel: t('NLSChatEmptyNoMessagesHelper'),
  archivedComposerPlaceholder: t('NLSChatComposerArchivedPlaceholder'),
  activeComposerPlaceholder: t('NLSChatComposerMessageAllPlaceholder'),
  lockedComposerPlaceholder: t('NLSChatComposerLockedPlaceholder'),
})

export const CHAT_CHANNEL_LIST_MODE_TABS = [
  { id: 'active', label: CHAT_SHELL_COPY.activeModeLabel },
  { id: 'archive', label: CHAT_SHELL_COPY.archiveModeLabel },
] as const

export type ChatChannelListModeTab = { id: 'active' | 'archive'; label: string }

export const getChatChannelListModeTabs = (t: ChatNlsT = defaultChatT): ChatChannelListModeTab[] => {
  const copy = getChatShellCopy(t)
  return [
    { id: 'active', label: copy.activeModeLabel },
    { id: 'archive', label: copy.archiveModeLabel },
  ]
}

export const getChatPageChromeClasses = (platform: TeamChatPlatform = 'desktop') => {
  const isMobileSurface = platform === 'member' || platform === 'helper'
  return {
    rootClass: isMobileSurface
      ? 'chatPage chatHubPage chatHubPage--mobile'
      : 'chatPage chatHubPage railPageCard',
    bodyClass: isMobileSurface ? 'chatHubBody chatHubBody--mobile' : 'chatHubBody',
  }
}

export const getChannelRailHeading = (showChannelArchive: boolean, canViewArchive: boolean, t: ChatNlsT = defaultChatT) => {
  const copy = getChatShellCopy(t)
  return (
  showChannelArchive && canViewArchive
    ? copy.archivedChannelsHeading
    : copy.activeChannelsHeading
  )
}

export const canShowCreateChannelButton = (platform: TeamChatPlatform, canCreateChannels: boolean) =>
  platform !== 'helper' && canCreateChannels

export const getChannelEmptyCopy = (showChannelArchive: boolean, t: ChatNlsT = defaultChatT) => {
  const copy = getChatShellCopy(t)
  return showChannelArchive ? copy.noArchivedChannels : copy.noChannels
}

export const getComposerPlaceholder = (input: { isActiveChannelArchived: boolean; canUseChat: boolean }, t: ChatNlsT = defaultChatT) => {
  const copy = getChatShellCopy(t)
  if (input.isActiveChannelArchived) return copy.archivedComposerPlaceholder
  return input.canUseChat
    ? copy.activeComposerPlaceholder
    : copy.lockedComposerPlaceholder
}

export const getEmptyChannelMessage = (platform: TeamChatPlatform, t: ChatNlsT = defaultChatT) => {
  const copy = getChatShellCopy(t)
  return (
  platform === 'helper'
    ? copy.emptyHelperChannel
    : copy.emptyDesktopChannel
  )
}

export const getChatRoleLabel = (t: ChatNlsT = defaultChatT, role: ChatAuthorRole): string => {
  switch (role) {
    case 'ADMIN':
      return t('NLSRoleTeamAdmin')
    case 'MEMBER':
      return t('NLSChatLabelRoleMember')
    case 'VIEWER':
      return t('NLSRoleViewer')
    case 'HELPER':
      return t('NLSRoleHelper')
    case 'BURNER':
      return t('NLSChatLabelRoleFieldPass')
    default:
      return role
  }
}

export const getChannelRowActions = (input: {
  showChannelArchive: boolean
  canManageArchive: boolean
  canManageChannels: boolean
  channelId: string
}) => {
  if (input.showChannelArchive) {
    return {
      canRestore: input.canManageArchive,
      canDeleteForever: input.canManageArchive && input.channelId !== DEFAULT_CHAT_CHANNEL_ID,
      canEdit: false,
      canArchive: false,
    }
  }
  return {
    canRestore: false,
    canDeleteForever: false,
    canEdit: input.canManageChannels,
    canArchive: input.canManageChannels && input.channelId !== DEFAULT_CHAT_CHANNEL_ID,
  }
}
